import React, { useState } from "react";

import { ToggleMenu, ToggleMenuItem } from "../../../components/ui/toggle-menu";

import { AddressInput } from "./AddressInput";
import { AddressList } from "./AddressList";
import { parseWalletConfig, serializeWalletConfig } from "./policyFixtures";

export interface WalletListInputProps {
    placeholder: string;
    modes?: { value: string; label: string; description: string }[];
    value: string;
    onChange: (next: string) => void;
    /** Sim-only cursor key placed on the address text field. */
    addressCursorKey?: string;
    /** Sim-only — force the address field's hover visual. */
    addressForceHover?: boolean;
    /** Sim-only — force the address field's focus visual. */
    addressForceFocus?: boolean;
    /** Sim-only cursor-key prefix placed on each mode ToggleMenuItem
     *  (`<prefix>-<mode>`), so a scripted cursor can travel to + "click" a
     *  specific mode (e.g. KYC Only). Inert in production. */
    modeCursorKeyPrefix?: string;
    /** Sim-only — the mode value whose ToggleMenuItem should paint its forced
     *  hover visual, or null. */
    modeHoverValue?: string | null;
    /** Sim-only — the mode value whose ToggleMenuItem should paint its forced
     *  pressed visual, or null. */
    modePressValue?: string | null;
}

/**
 * WalletListInput — config control for wallet-list policies. A 3-way mode
 * toggle picks Whitelist / Blacklist / KYC Only. In Whitelist or Blacklist
 * mode, pasting an address and "Add wallet" appends it to the list. In KYC
 * Only mode eligibility is gated on KYC-approved status instead of a manual
 * list — the manual address input + list are hidden and an explanatory line
 * is shown. The committed value is JSON (`{ mode, addresses }`). Light
 * validation only — a base58-ish length check.
 *
 * A Domain Component: it owns parse/serialize of the wallet policy value and
 * composes the `AddressInput` + `AddressList` GCs.
 */
export function WalletListInput({
    placeholder,
    modes,
    value,
    onChange,
    addressCursorKey,
    addressForceHover,
    addressForceFocus,
    modeCursorKeyPrefix,
    modeHoverValue,
    modePressValue
}: WalletListInputProps): React.ReactElement {
    const [draft, setDraft] = useState("");
    const fallbackMode = modes?.[0]?.value ?? "";
    const { mode, addresses } = parseWalletConfig(value, fallbackMode);
    const kycOnly = mode === "kyc-only";
    const valid = draft.trim().length >= 32;

    function setMode(nextMode: string): void {
        onChange(serializeWalletConfig({ mode: nextMode, addresses }));
    }

    function add(): void {
        const addr = draft.trim();
        if (addr.length < 32 || addresses.includes(addr)) {
            setDraft("");
            return;
        }
        onChange(
            serializeWalletConfig({
                mode,
                addresses: [...addresses, addr]
            })
        );
        setDraft("");
    }

    function remove(addr: string): void {
        onChange(
            serializeWalletConfig({
                mode,
                addresses: addresses.filter((a) => a !== addr)
            })
        );
    }

    return (
        <div>
            {modes && modes.length > 0 && (
                <ToggleMenu
                    value={mode}
                    onChange={(next) => next && setMode(next)}
                    spacing={1}
                    className="mb-3"
                >
                    {modes.map((m) => (
                        <ToggleMenuItem
                            key={m.value}
                            value={m.value}
                            variant="toggle"
                            size="sm"
                            data-sim-cursor-key={
                                modeCursorKeyPrefix
                                    ? `${modeCursorKeyPrefix}-${m.value}`
                                    : undefined
                            }
                            forceHover={modeHoverValue === m.value}
                            forcePressed={modePressValue === m.value}
                        >
                            {m.label}
                        </ToggleMenuItem>
                    ))}
                </ToggleMenu>
            )}
            {kycOnly ? (
                // KYC Only mode — eligibility is gated on KYC-approved status;
                // the manual address input + list are hidden. Leave a blank
                // spot the height of the address input so the panel doesn't
                // jump when switching to/from this mode.
                <div className="h-8" aria-hidden />
            ) : (
                <>
                    <AddressInput
                        value={draft}
                        onChange={setDraft}
                        onAdd={add}
                        placeholder={placeholder}
                        addLabel="Add wallet"
                        disabled={!valid}
                        data-sim-cursor-key={addressCursorKey}
                        forceHover={addressForceHover}
                        forceFocus={addressForceFocus}
                    />
                    <AddressList addresses={addresses} onRemove={remove} />
                </>
            )}
        </div>
    );
}
